// Reports mode's list page (`/c/{slug}/reports`; Reports / Ops UX pass 2026-10-21, Notion and
// Linear document lists): the 56 px location header, a 40 px toolbar (search, state filter,
// count) and one 36 px `ListRow` per report week (its newest version): week and range, state
// (Draft / Generated / Sent / Failed, Stale, Scheduled), version, recipients and the generated or
// sent time. Hover (always on coarse pointers): Open, Send, Schedule, PDF, New version, Copy
// link. The sidebar's WEEKS rows read the same query; managers get one more call, the company
// (the Friday schedule, for the upcoming week's "Scheduled" row). Client viewers get sent
// reports only (the server's rule). `j` / `k` walk the rows, `Enter` opens.
import {
  Badge,
  EmptyState,
  ListRow,
  LocationHeader,
  MetaCounts,
  SearchField,
  Segmented,
  Toolbar,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CalendarClockIcon,
  FileDownIcon,
  FileTextIcon,
  LinkIcon,
  RefreshCwIcon,
  SendIcon,
  Settings2Icon,
  UsersIcon,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { ReportListItem } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { currentWeek, weekRangeLabel } from "../../focus/weeks";
import { type CompanyAccess, isManager } from "../company";
import { formatDateTime, REPORT_STATE_LABELS } from "../format";
import { LinkButton, RowAction, RowLinkAction } from "../ops-ux";
import { companyQuery, keys, reportsQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { useFeature } from "../use-feature";
import { useListKeys } from "../work/list-keys";
import { latestByWeek, REPORT_STATE_VARIANT } from "./report-state";
import { UsageExportButton } from "./usage-export";

const FILTERS = ["all", "draft", "sent", "failed"] as const;
type Filter = (typeof FILTERS)[number];
const FILTER_LABEL: Record<Filter, string> = {
  all: "All",
  draft: "Draft",
  sent: "Sent",
  failed: "Failed",
};

/** A week's row: the newest version, or the upcoming week before its first version exists. */
type WeekRow = { week: string; record: ReportListItem | null };

const matches = (row: WeekRow, filter: Filter) => {
  if (filter === "all") return true;
  const state = row.record?.state ?? "draft";
  if (filter === "draft") return state === "draft" || state === "generated";
  return state === filter;
};

export function ReportWeeks({ api, company }: { api: Api; company: CompanyAccess }) {
  const reports = useInfiniteQuery(reportsQuery(api, company.id));
  const manager = isManager(company);
  const kpi = useFeature("kpi");
  const canAct = manager && !kpi.readOnly;
  // The Friday schedule (managers only): when this week's report is generated and whether it is
  // sent by itself, for the upcoming week's row.
  const detail = useQuery({ ...companyQuery(api, company.id), enabled: manager });
  const schedule = detail.data?.settings.friday_report;
  const generateAt = schedule?.generate_at ?? "15:00";
  const autoSend = schedule?.auto_send ?? false;
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const listRef = useRef<HTMLUListElement>(null);
  useListKeys(listRef, {
    onOpen: (row) => {
      const week = row.dataset.week;
      if (week)
        void navigate({
          to: "/c/$slug/kpis/reports/$week",
          params: { slug: company.slug, week },
          search: keepFocus,
        });
    },
  });

  const thisWeek = currentWeek();
  const rows = useMemo<WeekRow[]>(() => {
    const weeks = latestByWeek(reports.data?.pages.flatMap((p) => p.data) ?? []);
    const list: WeekRow[] = weeks.map((record) => ({ week: record.week_key, record }));
    // Managers see this week before its first version exists: the Friday job's row.
    if (manager && !weeks.some((w) => w.week_key === thisWeek))
      list.unshift({ week: thisWeek, record: null });
    return list;
  }, [reports.data, manager, thisWeek]);
  const needle = search.trim().toLowerCase();
  const shown = rows.filter(
    (r) =>
      matches(r, filter) &&
      (!needle ||
        r.week.toLowerCase().includes(needle) ||
        weekRangeLabel(r.week).toLowerCase().includes(needle)),
  );
  const sent = rows.filter((r) => r.record?.state === "sent").length;

  return (
    <div className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        icon={FileTextIcon}
        title="Friday reports"
        count={reports.isPending ? undefined : rows.length}
        actions={
          manager ? (
            <>
              {/* WOS-BUNDLE: the usage CSV for a date range. */}
              <UsageExportButton api={api} company={company} />
              <LinkButton
                className="max-md:hidden"
                render={
                  <Link
                    to="/c/$slug/setup/general"
                    params={{ slug: company.slug }}
                    search={keepFocus}
                    hash="friday-report"
                  />
                }
              >
                <Settings2Icon aria-hidden="true" />
                Settings
              </LinkButton>
              <LinkButton
                variant="default"
                render={
                  <Link
                    to="/c/$slug/kpis/reports/$week"
                    params={{ slug: company.slug, week: thisWeek }}
                    search={keepFocus}
                  />
                }
              >
                This week's report
              </LinkButton>
            </>
          ) : null
        }
      />
      <Toolbar bleed>
        <SearchField
          placeholder="Search weeks…"
          aria-label="Search weeks"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented
          size="sm"
          aria-label="State"
          value={filter}
          onValueChange={setFilter}
          options={FILTERS.map((f) => ({ value: f, label: FILTER_LABEL[f] }))}
        />
        <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums max-md:hidden">
          {sent} sent · {rows.length - sent} {rows.length - sent === 1 ? "other" : "others"}
        </span>
      </Toolbar>
      {reports.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading reports" />
        </div>
      ) : reports.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Reports" error={reports.error} />
        </div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-4 max-md:px-4">
          <EmptyState
            variant="card"
            icon={FileTextIcon}
            title="No Friday reports yet"
            description="Reports appear here once they have been sent."
          />
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-3 pb-6">
          <ul ref={listRef} data-slot="report-weeks" aria-label="Report weeks">
            {shown.map((row) => (
              <li key={row.week}>
                <WeekListRow
                  api={api}
                  company={company}
                  row={row}
                  canAct={canAct}
                  generateAt={generateAt}
                  autoSend={autoSend}
                />
              </li>
            ))}
            {shown.length === 0 ? (
              <li className="px-6 py-3 text-sm text-muted-foreground max-md:px-4">
                No week matches.
              </li>
            ) : null}
          </ul>
          <div className="px-6 max-md:px-4">
            <LoadMore
              what="reports"
              hasMore={reports.hasNextPage}
              loading={reports.isFetchingNextPage}
              onMore={() => void reports.fetchNextPage()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function WeekListRow({
  api,
  company,
  row,
  canAct,
  generateAt,
  autoSend,
}: {
  api: Api;
  company: CompanyAccess;
  row: WeekRow;
  canAct: boolean;
  generateAt: string;
  autoSend: boolean;
}) {
  const { week, record } = row;
  const params = { slug: company.slug, week };
  const upcoming = record === null || (record.state !== "sent" && week === currentWeek());
  const state = record?.state ?? "draft";
  const recipients = record?.recipients.length ?? 0;
  // The Friday job's line for a week not generated yet; the version's own time once it is.
  const schedule = `generates Friday ${generateAt}${autoSend ? " · sends 2 h later" : " · sent by a manager"}`;
  const meta = record
    ? record.sent_at
      ? `v${record.version} · sent ${formatDateTime(record.sent_at)}`
      : record.generated_at
        ? `v${record.version} · generated ${formatDateTime(record.generated_at)}`
        : `v${record.version} · ${upcoming ? schedule : "not generated"}`
    : schedule;
  return (
    <ListRow
      data-list-row=""
      data-week={week}
      data-state={state}
      lead={<FileTextIcon aria-hidden="true" />}
      title={
        // Phones get the short key ("W43 · Oct 19 – Oct 25") so the week number survives; the
        // link's accessible name is the full key at every width (`aria-label`).
        <>
          <span className="md:hidden">
            W{week.slice(-2)} · {weekRangeLabel(week)}
          </span>
          <span className="max-md:hidden">
            {week} · {weekRangeLabel(week)}
          </span>
        </>
      }
      render={
        <Link
          to="/c/$slug/kpis/reports/$week"
          params={params}
          search={keepFocus}
          aria-label={`${week} · ${weekRangeLabel(week)}`}
        />
      }
      chips={
        <>
          {record ? (
            <Badge variant={REPORT_STATE_VARIANT[record.state]}>
              {REPORT_STATE_LABELS[record.state]}
            </Badge>
          ) : null}
          {upcoming && autoSend ? (
            <Badge variant="info" data-slot="scheduled">
              <CalendarClockIcon aria-hidden="true" />
              Scheduled
            </Badge>
          ) : upcoming && !record ? (
            <Badge variant="neutral">Not generated</Badge>
          ) : null}
          {canAct && record?.report_stale && record.state === "generated" ? (
            <Badge variant="warning">Stale</Badge>
          ) : null}
        </>
      }
      meta={meta}
      trailing={
        <MetaCounts
          items={[{ icon: UsersIcon, count: recipients, label: "recipients" }]}
          className="w-10 justify-end max-md:hidden"
        />
      }
      actions={
        <>
          <RowLinkAction
            icon={ArrowUpRightIcon}
            label={`Open ${week}`}
            render={<Link to="/c/$slug/kpis/reports/$week" params={params} search={keepFocus} />}
          />
          {canAct ? (
            <>
              <RowLinkAction
                icon={SendIcon}
                label={`Send ${week}`}
                render={
                  <Link
                    to="/c/$slug/kpis/reports/$week"
                    params={params}
                    search={keepFocus}
                    hash="send"
                  />
                }
              />
              <RowLinkAction
                icon={CalendarClockIcon}
                label={`Schedule ${week}`}
                render={
                  <Link
                    to="/c/$slug/kpis/reports/$week"
                    params={params}
                    search={keepFocus}
                    hash="schedule"
                  />
                }
              />
              <NewVersionAction api={api} co={company.id} week={week} />
            </>
          ) : null}
          {record?.pdf_object_key ? <PdfAction api={api} co={company.id} record={record} /> : null}
          <CopyLinkAction slug={company.slug} week={week} />
        </>
      }
    />
  );
}

function NewVersionAction({ api, co, week }: { api: Api; co: string; week: string }) {
  const qc = useQueryClient();
  const generate = useMutation({
    mutationFn: () => api.client.reports.generate({ params: { co }, body: { week_key: week } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.reports(co) });
      toast.add({ title: `Version ${r.version} of ${week} generated`, type: "success" });
    },
  });
  return (
    <RowAction
      icon={RefreshCwIcon}
      label={`New version of ${week}`}
      disabled={generate.isPending}
      onClick={() => generate.mutate()}
    />
  );
}

/** Downloads the version's PDF (a blob through the SDK, so the session cookie goes along). */
export function usePdfDownload(api: Api, co: string, record: ReportListItem) {
  return useMutation({
    mutationFn: () => api.client.reports.pdf({ params: { co, id: record.id } }),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `friday-report-${record.week_key}-v${record.version}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });
}

function PdfAction({ api, co, record }: { api: Api; co: string; record: ReportListItem }) {
  const download = usePdfDownload(api, co, record);
  return (
    <RowAction
      icon={FileDownIcon}
      label={`Download PDF of ${record.week_key}`}
      disabled={download.isPending}
      onClick={() => download.mutate()}
    />
  );
}

export function CopyLinkAction({ slug, week }: { slug: string; week: string }) {
  return (
    <RowAction
      icon={LinkIcon}
      label={`Copy link to ${week}`}
      onClick={() => {
        const href = `${window.location.origin}/c/${slug}/kpis/reports/${week}`;
        void navigator.clipboard?.writeText(href).then(
          () => toast.add({ title: "Link copied", description: href, type: "success" }),
          () => toast.add({ title: "The link could not be copied", description: href }),
        );
      }}
    />
  );
}
